import MoodOutlinedIcon from '@mui/icons-material/MoodOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import DirectionsRunOutlinedIcon from '@mui/icons-material/DirectionsRunOutlined';
import LunchDiningOutlinedIcon from '@mui/icons-material/LunchDiningOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import CelebrationOutlinedIcon from '@mui/icons-material/CelebrationOutlined';
import SentimentDissatisfiedOutlinedIcon from '@mui/icons-material/SentimentDissatisfiedOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import SentimentSatisfiedAltOutlinedIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import MoodBadOutlinedIcon from '@mui/icons-material/MoodBadOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import BedtimeOutlinedIcon from '@mui/icons-material/BedtimeOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import ParkOutlinedIcon from '@mui/icons-material/ParkOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import BakeryDiningOutlinedIcon from '@mui/icons-material/BakeryDiningOutlined';
import DinnerDiningOutlinedIcon from '@mui/icons-material/DinnerDiningOutlined';
import LocalCafeOutlinedIcon from '@mui/icons-material/LocalCafeOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import MedicationOutlinedIcon from '@mui/icons-material/MedicationOutlined';
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import HealingOutlinedIcon from '@mui/icons-material/HealingOutlined';
import ConstructionOutlinedIcon from '@mui/icons-material/ConstructionOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { ACTIVITY_TYPE_OPTIONS } from '../../constants/activityThemes';

// Configuration for Daily Care actions
export const getDailyCareConfig = (actionType, child) => {
  const configs = {
    mood: {
      title: 'Detailed Mood Check',
      icon: null,
      iconComponent: MoodOutlinedIcon,
      description: `Comprehensive mood tracking for ${child?.name}`,
      fields: [
        {
          key: 'moodLevel',
          label: 'Current Mood',
          description: 'Rate their overall emotional state',
          type: 'scale',
          min: 1,
          max: 5,
          labels: ['😢 Very upset', '😕 Upset', '😐 Neutral', '🙂 Happy', '😊 Very happy'],
          required: true,
          defaultValue: 3,
        },
        {
          key: 'moodTags',
          label: 'What describes their mood?',
          description: 'Select all that apply',
          type: 'chips',
          multiple: true,
          options: [
            { label: 'Calm', value: 'calm', emoji: null, iconComponent: SpaOutlinedIcon },
            { label: 'Excited', value: 'excited', emoji: null, iconComponent: CelebrationOutlinedIcon },
            { label: 'Frustrated', value: 'frustrated', emoji: null, iconComponent: SentimentDissatisfiedOutlinedIcon },
            { label: 'Anxious', value: 'anxious', emoji: null, iconComponent: PsychologyOutlinedIcon },
            { label: 'Content', value: 'content', emoji: null, iconComponent: SentimentSatisfiedAltOutlinedIcon },
            { label: 'Irritable', value: 'irritable', emoji: null, iconComponent: MoodBadOutlinedIcon },
          ],
          required: false,
        },
        {
          key: 'notes',
          label: 'Additional Notes',
          description: 'Any specific observations or context?',
          type: 'text',
          multiline: true,
          rows: 3,
          placeholder: 'e.g., Had a great morning, seems tired after lunch...',
          required: false,
        }
      ]
    },

    energy: {
      title: 'Detailed Energy Level',
      icon: null,
      iconComponent: BoltOutlinedIcon,
      description: `Comprehensive energy tracking for ${child?.name}`,
      fields: [
        {
          key: 'energyLevel',
          label: 'Overall Energy Level',
          description: 'How energetic are they today?',
          type: 'scale',
          min: 1,
          max: 5,
          labels: ['😴 Very low', '😑 Low', '😐 Normal', '😊 High', '⚡ Very high'],
          required: true,
          defaultValue: 3,
        },
        {
          key: 'energyPattern',
          label: 'Energy Pattern',
          description: 'How has their energy changed throughout the day?',
          type: 'chips',
          options: [
            { label: 'Steady all day', value: 'steady', emoji: null, iconComponent: TrendingUpOutlinedIcon },
            { label: 'High in morning', value: 'morning_high', emoji: null, iconComponent: WbSunnyOutlinedIcon },
            { label: 'Crash after lunch', value: 'afternoon_crash', emoji: null, iconComponent: BedtimeOutlinedIcon },
            { label: 'Second wind evening', value: 'evening_boost', emoji: null, iconComponent: BedtimeOutlinedIcon },
            { label: 'Up and down', value: 'fluctuating', emoji: null, iconComponent: QueryStatsOutlinedIcon },
          ],
          required: false,
        },
        {
          key: 'activities',
          label: 'Main Activities Today',
          description: 'What did they spend most time doing?',
          type: 'chips',
          multiple: true,
          options: [
            { label: 'Physical play', value: 'physical_play', emoji: null, iconComponent: DirectionsRunOutlinedIcon },
            { label: 'Screen time', value: 'screen_time', emoji: null, iconComponent: SmartphoneOutlinedIcon },
            { label: 'Creative activities', value: 'creative', emoji: null, iconComponent: PaletteOutlinedIcon },
            { label: 'Outdoor activities', value: 'outdoor', emoji: null, iconComponent: ParkOutlinedIcon },
            { label: 'Quiet activities', value: 'quiet', emoji: null, iconComponent: MenuBookOutlinedIcon },
            { label: 'Social interaction', value: 'social', emoji: null, iconComponent: GroupsOutlinedIcon },
          ],
          required: false,
        }
      ]
    },

    activity: {
      title: 'Activity Check',
      icon: null,
      iconComponent: DirectionsRunOutlinedIcon,
      description: `Capture therapy sessions, outings, and routines for ${child?.name}`,
      fields: [
        {
          key: 'activityTypes',
          label: 'What did they do?',
          type: 'chips',
          multiple: false,
          options: ACTIVITY_TYPE_OPTIONS,
          required: true,
        },
        {
          key: 'engagement',
          label: 'How engaged were they?',
          type: 'scale',
          min: 1,
          max: 5,
          labels: ['Low', 'Okay', 'Good', 'Great', 'Fully engaged'],
          required: true,
        },
        {
          key: 'notes',
          label: 'Activity Notes',
          description: 'Any useful context about the activity?',
          type: 'text',
          multiline: true,
          rows: 3,
          placeholder: 'e.g., Went to the park after speech therapy, calm and focused...',
          required: false,
        },
      ],
    },

    food_health: {
      title: 'Food & Medicine',
      icon: null,
      iconComponent: LunchDiningOutlinedIcon,
      description: `Track ${child?.name}'s nutrition and health`,
      fields: [
        {
          key: 'mealsEaten',
          label: 'Meals & Snacks Today',
          description: 'What did they eat?',
          type: 'chips',
          multiple: true,
          options: [
            { label: 'Breakfast', value: 'breakfast', emoji: null, iconComponent: BakeryDiningOutlinedIcon },
            { label: 'Lunch', value: 'lunch', emoji: null, iconComponent: LunchDiningOutlinedIcon },
            { label: 'Dinner', value: 'dinner', emoji: null, iconComponent: DinnerDiningOutlinedIcon },
            { label: 'Morning snack', value: 'morning_snack', emoji: null, iconComponent: RestaurantOutlinedIcon },
            { label: 'Afternoon snack', value: 'afternoon_snack', emoji: null, iconComponent: LocalCafeOutlinedIcon },
            { label: 'Evening snack', value: 'evening_snack', emoji: null, iconComponent: BakeryDiningOutlinedIcon },
          ],
          required: true,
        },
        {
          key: 'appetite',
          label: 'Appetite Level',
          description: 'How was their appetite today?',
          type: 'scale',
          min: 1,
          max: 5,
          labels: ['🚫 No appetite', '😕 Poor', '😐 Normal', '🙂 Good', '😋 Excellent'],
          required: true,
          defaultValue: 3,
        },
        {
          key: 'foodChallenges',
          label: 'Any eating challenges?',
          type: 'chips',
          multiple: true,
          options: [
            { label: 'Refused meals', value: 'meal_refusal', emoji: null, iconComponent: BlockOutlinedIcon },
            { label: 'Picky eating', value: 'picky_eating', emoji: null, iconComponent: MoodBadOutlinedIcon },
            { label: 'Sensory issues', value: 'sensory_issues', emoji: null, iconComponent: PsychologyOutlinedIcon },
            { label: 'Digestive issues', value: 'digestive_issues', emoji: null, iconComponent: ReportProblemOutlinedIcon },
            { label: 'No issues', value: 'none', emoji: null, iconComponent: CheckCircleOutlinedIcon },
          ],
          required: false,
        },
        {
          key: 'medicationsTaken',
          label: 'Medications Today',
          description: 'Were medications taken as scheduled?',
          type: 'chips',
          multiple: true,
          options: [
            { label: 'Morning meds', value: 'morning_meds', emoji: null, iconComponent: WbSunnyOutlinedIcon },
            { label: 'Afternoon meds', value: 'afternoon_meds', emoji: null, iconComponent: WbSunnyOutlinedIcon },
            { label: 'Evening meds', value: 'evening_meds', emoji: null, iconComponent: BedtimeOutlinedIcon },
            { label: 'As needed meds', value: 'prn_meds', emoji: null, iconComponent: MedicationOutlinedIcon },
            { label: 'No medications', value: 'none', emoji: null, iconComponent: RemoveOutlinedIcon },
          ],
          required: false,
        }
      ]
    },

    safety: {
      title: 'Safety Check',
      icon: null,
      iconComponent: ShieldOutlinedIcon,
      description: `Safety check for ${child?.name}`,
      fields: [
        {
          key: 'safetyStatus',
          label: 'Overall Safety Status',
          description: 'Any safety concerns today?',
          type: 'chips',
          options: [
            { label: 'All good', value: 'all_good', emoji: null, iconComponent: CheckCircleOutlinedIcon },
            { label: 'Minor incident', value: 'minor_incident', emoji: null, iconComponent: ReportProblemOutlinedIcon },
            { label: 'Needs attention', value: 'needs_attention', emoji: null, iconComponent: ErrorOutlineOutlinedIcon },
          ],
          required: true,
        },
        {
          key: 'incidentType',
          label: 'Type of Incident (if any)',
          type: 'chips',
          multiple: true,
          options: [
            { label: 'Minor injury', value: 'minor_injury', emoji: null, iconComponent: HealingOutlinedIcon },
            { label: 'Behavioral concern', value: 'behavioral_concern', emoji: null, iconComponent: ReportProblemOutlinedIcon },
            { label: 'Environmental hazard', value: 'environmental_hazard', emoji: null, iconComponent: ConstructionOutlinedIcon },
            { label: 'Medication error', value: 'medication_error', emoji: null, iconComponent: MedicationOutlinedIcon },
            { label: 'Other', value: 'other', emoji: null, iconComponent: HelpOutlineOutlinedIcon },
          ],
          required: false,
        },
        {
          key: 'incidentDetails',
          label: 'Incident Details',
          description: 'Describe what happened and actions taken',
          type: 'text',
          multiline: true,
          rows: 4,
          placeholder: 'Describe the incident, response taken, and any follow-up needed...',
          required: false,
        },
        {
          key: 'followUpNeeded',
          label: 'Follow-up Required?',
          type: 'chips',
          options: [
            { label: 'No follow-up needed', value: 'none', emoji: null, iconComponent: CheckCircleOutlinedIcon },
            { label: 'Monitor closely', value: 'monitor', emoji: null, iconComponent: VisibilityOutlinedIcon },
            { label: 'Contact healthcare provider', value: 'contact_healthcare', emoji: null, iconComponent: LocalHospitalOutlinedIcon },
            { label: 'Update safety plan', value: 'update_safety_plan', emoji: null, iconComponent: AssignmentOutlinedIcon },
          ],
          required: false,
        }
      ]
    }
  };

  return configs[actionType] || null;
};
